import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { mdiAlertCircleOutline } from "@mdi/js";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "sonner";

import { cn } from "@/src/lib/utils";

import { CONTENT_TYPE } from "./Type";
import { CONTENT_MAX_WIDTH_STYLE } from "../../constants/size";
import ContentContext from "../../contexts/ContentContext";
import "../../css/admin/content-page.scss";
import "../../css/admin/utils/item-status.scss";
import useStuckAttribute from "../../hooks/useStuckAttribute";
import useUrlTab from "../../hooks/useUrlTab";
import { computeAnalyticPreviewAndEmbeddedLinks } from "../../utils/data/Content";
import { getFreemiumEndErrorMessage } from "../../utils/freemium";
import {
  NAV_TABS_BAR_CLASSES,
  NAV_TABS_LIST_CLASSES,
  NAV_TABS_TRIGGER_CLASSES,
} from "../common/navTabsStyles";
import PageHeader from "../common/PageHeader";
import TabContentAvailability from "../contentTabs/TabContentAvailability";
import TabContentCasting from "../contentTabs/TabContentCasting";
import TabContentCuePoints from "../contentTabs/TabContentCuePoints";
import TabContentLanguages from "../contentTabs/TabContentLanguages";
import TabContentMedia from "../contentTabs/TabContentMedia";
import TabContentMetadata from "../contentTabs/TabContentMetadata";
import TabContentMetaLanguages from "../contentTabs/TabContentMetaLanguages";
import TabContentRights from "../contentTabs/TabContentRights";
import ConfirmModal from "../modal/ConfirmModal";
import { CONTENTS_ROUTE } from "../routes/routes";
import Tooltip from "../tooltip/Tooltip";
import { ScrollArea } from "../ui/scroll-area";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs";
import ContainerHeader from "../utils/ContainerHeader";
import Spinner from "../utils/Spinner";

import type { JSX } from "react";

import type { GetMediaResponse, MediaChildren } from "../../api/MediaAPI.types";
import type { AppState } from "../../reducers";
import type {
  CastingsState,
  MetadatasState,
  SmartlistsState,
} from "../../reducers/fetch.types";
import type { MediaTranslationsHandle } from "../contentTabs/media/MediaTranslations";

const { MASTER, HOSTED, LIVE } = CONTENT_TYPE;

const navigationHeaderLinks = {
  en: "https://support.okast.tv/en/support/solutions/articles/103000097696-creation-management-of-your-content",
  fr: "https://support.okast.tv/fr/support/solutions/articles/103000097696-cr%C3%A9ation-et-gestion-de-contenus",
};

export interface ContentContextValues {
  item: GetMediaResponse;
  isNew: boolean;
  isSaved: boolean;
  smartlists: SmartlistsState;
  isDisabled: boolean;
  metadatas: MetadatasState;
  castings: CastingsState;
  languageId: string;
  onItemUpdate: (updatedItem: GetMediaResponse) => void;
  onSave: (showMessage?: boolean) => void;
  translationsRef: React.RefObject<MediaTranslationsHandle | null>;
  childTranslationsRefs: React.RefObject<Map<
    string,
    MediaTranslationsHandle
  > | null>;
}

const TABS = {
  ENCODE: "media",
  DESCRIPTION: "description",
  LANGUAGES: "languages",
  CASTING: "casting",
  METALANGUAGES: "meta-languages",
  RIGHTS: "rights",
  AVAILABILITY: "availability",
  CUEPOINTS: "cuepoints",
} as const;

type Tab = (typeof TABS)[keyof typeof TABS];

interface ContentTabsProps {
  isDisabled?: boolean;
  isNew?: boolean;
  isSaved: boolean;
  setIsSaved: (isSaved: boolean) => void;
  item: GetMediaResponse;
  setItem: (item: GetMediaResponse) => void;
  onArchive?: () => void;
  onSubmit: (
    item: GetMediaResponse,
    disableValidation?: boolean,
    showMessage?: boolean,
    onSuccess?: () => void,
  ) => void;
  ownerRole?: boolean;
  fastAssetsMp4Status?: string | null;
  isLoading?: boolean;
  isSaving?: boolean;
}

export default function ContentTabs({
  isDisabled = false,
  isNew = false,
  isSaved,
  setIsSaved,
  fastAssetsMp4Status = null,
  item,
  setItem,
  onArchive,
  onSubmit,
  ownerRole,
  isLoading,
  isSaving,
}: ContentTabsProps): JSX.Element {
  const { t, i18n } = useTranslation(["editor-content"]);

  const metadatas = useSelector(
    (state: AppState) => state.fetchReducer.metadatas,
  );
  const smartlists = useSelector(
    (state: AppState) => state.fetchReducer.smartlists,
  );
  const castings = useSelector(
    (state: AppState) => state.fetchReducer.castings,
  );
  const projects = useSelector(
    (state: AppState) => state.fetchReducer.projects,
  );
  const orgaConfig = useSelector(
    (state: AppState) => state.fetchReducer.orga_config,
  );
  const lang = useSelector((state: AppState) => state.variableReducer.lang);

  const languageId = useMemo(() => {
    let uuid = "";
    if (metadatas?.isFetched && metadatas?.data?.languages) {
      const { indexes, data } = metadatas.data.languages;
      if (lang && indexes?.part1?.[lang]?.[0] !== undefined) {
        const languageIdx = indexes.part1[lang][0];
        if (data?.[languageIdx]?.uuid) {
          uuid = data[languageIdx].uuid;
        }
      }
    }

    return uuid;
  }, [metadatas, lang]);

  const [pendingTab, setPendingTab] = useState<Tab | null>(null);

  const isFastAssetsTranscoded = fastAssetsMp4Status === "transcoded";
  const isArchived = item.status === "archived";
  const status = isArchived ? "archived" : item.publication_status;
  const hasOfferInProject = item.offers?.length > 0;
  const isAudioOnly = item.audio_only;
  const hasFastOption = orgaConfig?.data?.organization_options?.allow_fast;
  const showCuePointsTab =
    item.type === HOSTED && !isAudioOnly && hasFastOption;

  const availableTabs = useMemo(() => {
    const tabs: Tab[] = [
      TABS.ENCODE,
      TABS.LANGUAGES,
      TABS.DESCRIPTION,
      TABS.CASTING,
      TABS.METALANGUAGES,
      TABS.RIGHTS,
      TABS.AVAILABILITY,
    ];
    if (showCuePointsTab) {
      tabs.push(TABS.CUEPOINTS);
    }
    return tabs;
  }, [showCuePointsTab]);

  const { activeTab, setActiveTab } = useUrlTab({
    availableTabs,
    defaultTab: TABS.ENCODE,
  });

  const { previewLinks, analyticLinks, embeddedItems } = useMemo(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    () => computeAnalyticPreviewAndEmbeddedLinks(item, projects),
    [item, projects],
  );

  const { breadcrumbsTitle, contentUrl } = useMemo(() => {
    let title = "";
    let url = "/";
    switch (item?.type) {
      case "live":
        title = t("liveTitle");
        url = `${CONTENTS_ROUTE}/live`;
        break;
      case "external":
        title = t("externalTitle");
        url = `${CONTENTS_ROUTE}/external`;
        break;
      case "fast":
        title = t("fastTitle");
        url = `${CONTENTS_ROUTE}/fast`;
        break;
      case "master":
      case "hosted":
      default:
        title = t("hostedTitle");
        url = `${CONTENTS_ROUTE}/hosted`;
        break;
    }
    return { breadcrumbsTitle: title, contentUrl: url };
  }, [item?.type, t]);

  const breadcrumbs = useMemo(
    () => [
      { name: breadcrumbsTitle, url: contentUrl },
      { name: t(isNew ? "new" : "edit") },
    ],
    [breadcrumbsTitle, contentUrl, isNew, t],
  );

  const displayLabel = item.label ?? t("unamedContent");

  const onChangeTitle = useCallback(
    (
      target: React.ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement
      >["target"],
    ) => {
      setItem({ ...item, label: target.value });
    },
    [item, setItem],
  );

  const onChangeState = useCallback(
    (enabled: boolean) => {
      setIsSaved(false);
      setItem({ ...item, status: enabled ? "enabled" : "disabled" });
    },
    [item, setItem, setIsSaved],
  );

  const tabBarRef = useStuckAttribute<HTMLDivElement>();
  const translationsRef = useRef<MediaTranslationsHandle>(null);
  const childTranslationsRefs = useRef<Map<string, MediaTranslationsHandle>>(
    new Map(),
  );

  const handleSave = useCallback(
    async (showMessage?: boolean) => {
      // For master media, validate all child translation forms
      if (item.type === "master" && item.children?.length) {
        const childValidations = await Promise.all(
          item.children.map(
            async (child) =>
              childTranslationsRefs.current.get(child.uuid)?.isValid() ??
              Promise.resolve(true),
          ),
        );

        const invalidChildren = item.children.filter(
          (_, i) => !childValidations[i],
        );

        if (invalidChildren.length > 0) {
          toast.error(
            t("form:validationErrorChildren", {
              childNames: invalidChildren.map((c) => c.label).join(", "),
              count: invalidChildren.length,
              ns: "form",
            }),
          );
          return;
        }
      }

      const isValidTranslations =
        (await translationsRef.current?.isValid()) ?? true;
      if (!isValidTranslations) return;

      const freemiumEndValues =
        item.type === "master"
          ? (item.children ?? []).map((child) => child.freemium_end ?? "")
          : [item.freemium_end ?? ""];
      const freemiumEndErrorMessage = freemiumEndValues
        .map((value) =>
          getFreemiumEndErrorMessage(value, item.source_duration_ms),
        )
        .find((message) => message !== null);
      if (freemiumEndErrorMessage) {
        toast.error(freemiumEndErrorMessage);
        return;
      }

      // eslint-disable-next-line @typescript-eslint/naming-convention
      const { translations, translations_indexes } =
        translationsRef.current?.getApiTranslations?.() ?? {
          translations: item.translations,

          translations_indexes: item.translations_indexes,
        };

      const handleSuccess = (): void => {
        translationsRef.current?.onSaveSuccess();
        item.children?.forEach((child) => {
          childTranslationsRefs.current.get(child.uuid)?.onSaveSuccess();
        });
      };

      onSubmit(
        {
          ...item,
          translations:
            translations as unknown as GetMediaResponse["translations"],

          translations_indexes,
          children: item.children?.map((child) => {
            const childApiTranslations = childTranslationsRefs.current
              .get(child.uuid)
              ?.getApiTranslations?.();
            if (!childApiTranslations) return child;
            const defaultTranslation = childApiTranslations.translations.find(
              (translation) => translation.default,
            );
            return {
              ...child,
              label: defaultTranslation?.name ?? child.label,
              translations:
                childApiTranslations.translations as unknown as MediaChildren["translations"],

              translations_indexes: childApiTranslations.translations_indexes,
            };
          }),
        },
        false,
        showMessage,
        handleSuccess,
      );
    },
    [item, onSubmit, t],
  );

  const handleItemUpdate = useCallback(
    (updatedItem: GetMediaResponse) => {
      setIsSaved(false);
      setItem(updatedItem);
    },
    [setItem, setIsSaved],
  );

  const handleTabChange = useCallback(
    (newValue: Tab) => {
      if (activeTab !== TABS.LANGUAGES) {
        setActiveTab(newValue);
        return;
      }

      const isTranslationsDirty =
        item.type === "master"
          ? Array.from(childTranslationsRefs.current.values()).some((ref) =>
              ref.isDirty(),
            )
          : (translationsRef.current?.isDirty() ?? false);

      if (isTranslationsDirty) {
        setPendingTab(newValue);
      } else {
        setActiveTab(newValue);
      }
    },
    [
      activeTab,
      item.type,
      childTranslationsRefs,
      translationsRef,
      setActiveTab,
    ],
  );

  const handleConfirmTabChange = useCallback(() => {
    if (pendingTab) {
      setActiveTab(pendingTab);
      setPendingTab(null);
    }
  }, [pendingTab, setActiveTab]);

  const handleCancelTabChange = useCallback(() => {
    setPendingTab(null);
  }, []);

  const contextValue = useMemo(
    () => ({
      item,
      isNew,
      isSaved,
      smartlists,
      isDisabled,
      metadatas,
      castings,
      languageId,
      onItemUpdate: handleItemUpdate,
      onSave: handleSave,
      translationsRef,
      childTranslationsRefs,
    }),
    [
      item,
      isNew,
      isSaved,
      smartlists,
      isDisabled,
      metadatas,
      castings,
      languageId,
      handleItemUpdate,
      handleSave,
    ],
  );

  useEffect(() => {
    moment.locale(
      i18n.language || (i18n.options?.fallbackLng as string) || "en",
    );
  }, [i18n.language, i18n.options?.fallbackLng]);

  const isHostedEncodeTab = activeTab === TABS.ENCODE && item.type === HOSTED;
  const isLiveEncodeTab = activeTab === TABS.ENCODE && item.type === LIVE;
  const isEncodeTabWithOwnLayout = isHostedEncodeTab || isLiveEncodeTab;
  const isRightsTab = activeTab === TABS.RIGHTS;
  const isMaster = item.type === MASTER;
  const isRightsTabWithoutMaster =
    activeTab === TABS.RIGHTS && item.type !== MASTER;

  const tabContentClassName = cn(
    !isLiveEncodeTab && "drop-shadow-sm",
    isHostedEncodeTab && "mt-0! p-0!",
    (isRightsTab || isLiveEncodeTab) && "mt-5",
    !isEncodeTabWithOwnLayout &&
      !isRightsTabWithoutMaster &&
      "content-tab-container",
    isMaster && "[&>div]:gap-4",
  );

  return (
    <React.Fragment>
      <ContentContext.Provider value={contextValue}>
        <div
          className="content-page mx-auto max-w-(--maxWidth)"
          style={CONTENT_MAX_WIDTH_STYLE}
        >
          <PageHeader
            breadcrumbs={breadcrumbs}
            infoPanelLinks={navigationHeaderLinks}
          />
          <ContainerHeader
            withBackBtn
            analyticsLink={analyticLinks}
            canSave={!isDisabled}
            defaultLabel={t("unamedContent")}
            embedded={item.type !== MASTER ? embeddedItems : undefined}
            hasComments={item.comments}
            isSaving={isSaving}
            item={{ ...item, label: displayLabel }}
            itemStatus={status}
            onArchive={onArchive}
            onChangeLabel={onChangeTitle}
            onChangeState={onChangeState}
            onSave={handleSave}
            ownerRole={ownerRole}
            previewDisabled={status !== "active" || !hasOfferInProject}
            previewLink={item.type !== MASTER ? previewLinks : undefined}
            type="content"
          />
          <div>
            <Tabs
              ref={tabBarRef}
              className={cn(NAV_TABS_BAR_CLASSES, "mb-1")}
              onValueChange={(value) => handleTabChange(value as Tab)}
              value={activeTab}
            >
              {/* The viewport overlaps the bar's bottom border by 1px so it doesn't clip the active underline; the scrollbar is lifted above that underline */}
              <ScrollArea
                className="-mb-px w-full [&_[data-slot=scroll-area-scrollbar]]:bottom-[3px]! [&_[data-slot=scroll-area-viewport]]:pb-px [&_[data-slot=scroll-area-viewport]>div]:flex! [&_[data-slot=scroll-area-viewport]>div]:justify-center-safe!"
                orientation="horizontal"
              >
                <TabsList className={NAV_TABS_LIST_CLASSES} variant="line">
                  <TabsTrigger
                    className={NAV_TABS_TRIGGER_CLASSES}
                    disabled={isLoading}
                    value={TABS.ENCODE}
                  >
                    {t("tabMedia")}
                  </TabsTrigger>
                  {showCuePointsTab ? (
                    <span
                      className="inline-flex h-full"
                      data-tooltip-content={t("transcodedStreamFastIsRequired")}
                      data-tooltip-hidden={isFastAssetsTranscoded}
                      data-tooltip-id="content-tab-tooltip"
                    >
                      <TabsTrigger
                        className={NAV_TABS_TRIGGER_CLASSES}
                        disabled={!isFastAssetsTranscoded || isLoading}
                        value={TABS.CUEPOINTS}
                      >
                        {t("tabCuePoints")}
                      </TabsTrigger>
                    </span>
                  ) : null}
                  <TabsTrigger
                    className={NAV_TABS_TRIGGER_CLASSES}
                    disabled={isLoading}
                    value={TABS.LANGUAGES}
                  >
                    {t("tabLanguages")}
                  </TabsTrigger>
                  <TabsTrigger
                    className={NAV_TABS_TRIGGER_CLASSES}
                    disabled={isLoading}
                    value={TABS.DESCRIPTION}
                  >
                    {t("tabDescription")}
                  </TabsTrigger>
                  <TabsTrigger
                    className={NAV_TABS_TRIGGER_CLASSES}
                    disabled={isLoading}
                    value={TABS.CASTING}
                  >
                    {t("tabCasting")}
                  </TabsTrigger>
                  <TabsTrigger
                    className={NAV_TABS_TRIGGER_CLASSES}
                    disabled={isLoading}
                    value={TABS.METALANGUAGES}
                  >
                    {t("tabMetaLanguages")}
                  </TabsTrigger>
                  <TabsTrigger
                    className={NAV_TABS_TRIGGER_CLASSES}
                    disabled={isLoading}
                    value={TABS.RIGHTS}
                  >
                    {t("tabRights")}
                  </TabsTrigger>
                  <TabsTrigger
                    className={NAV_TABS_TRIGGER_CLASSES}
                    disabled={isLoading}
                    value={TABS.AVAILABILITY}
                  >
                    {t("tabAvailability")}
                  </TabsTrigger>
                </TabsList>
              </ScrollArea>
            </Tabs>
            <div className={tabContentClassName}>
              {isLoading ? (
                <div className="flex h-125 items-center justify-center">
                  <Spinner />
                </div>
              ) : null}
              {activeTab === TABS.ENCODE && <TabContentMedia />}
              {activeTab === TABS.CUEPOINTS && showCuePointsTab ? (
                <TabContentCuePoints />
              ) : null}
              {activeTab === TABS.LANGUAGES && <TabContentLanguages />}
              {activeTab === TABS.DESCRIPTION && <TabContentMetadata />}
              {activeTab === TABS.CASTING && <TabContentCasting />}
              {activeTab === TABS.METALANGUAGES && <TabContentMetaLanguages />}
              {activeTab === TABS.RIGHTS && <TabContentRights />}
              {activeTab === TABS.AVAILABILITY && <TabContentAvailability />}
            </div>
          </div>
        </div>
      </ContentContext.Provider>
      <Tooltip id="content-tab-tooltip" />
      <ConfirmModal
        iconPath={mdiAlertCircleOutline}
        isOpen={pendingTab !== null}
        onClose={handleCancelTabChange}
        title={t("unsavedTranslationsTitle")}
        variant="primary"
        actions={[
          {
            label: t("unsavedTranslationsCancel"),
            onClick: handleCancelTabChange,
            isCloseAfterAction: true,
          },
          {
            label: t("unsavedTranslationsConfirm"),
            onClick: handleConfirmTabChange,
            buttonVariant: "secondary",
            isCloseAfterAction: true,
          },
        ]}
      >
        {t("unsavedTranslationsMessage")}
      </ConfirmModal>
    </React.Fragment>
  );
}
